/**
 * Header — топ-бар. Баланс, переключатель языка интерфейса, аватар.
 *
 * UX-J1 (2026-07-27): добавлен слот для Breadcrumbs слева (App > Section > Page).
 * ProjectPicker переехал в нижний ряд (под крошками) — узкие экраны не ломаются,
 * а на широких всё умещается в одну строку благодаря flex-wrap.
 */
import { Coins, Globe, Bell, User, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { useAuthStore } from '@/store/authStore';
import { useMyBalance } from '@/hooks/useAccount';
import { Breadcrumbs } from './Breadcrumbs';
import { ProjectPicker } from './ProjectPicker';
import { ThemeToggle } from './ThemeToggle';
import { WorkspaceLibraryBar } from '@/components/libraries/WorkspaceLibraryBar';

export function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const account = useAuthStore((s) => s.account);
  const logout = useAuthStore((s) => s.logout);
  const { data: balance } = useMyBalance();
  const [menuOpen, setMenuOpen] = useState(false);

  const available = balance ? parseFloat(balance.available_balance) : 0;
  const reserved = balance ? parseFloat(balance.reserved_balance) : 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = (account?.full_name ?? '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className="h-14 px-6 border-b border-surface-800 flex items-center justify-between bg-surface-950/80 backdrop-blur gap-4">
      <div className="text-sm text-surface-400 flex items-center gap-4 min-w-0">
        <Breadcrumbs />
        <ProjectPicker />
        <WorkspaceLibraryBar />
      </div>

      <div className="flex items-center gap-2">
        {balance && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-warning-tint/10 border border-warning-tint/20 text-warning text-sm">
            <Coins className="w-4 h-4" />
            <span className="font-mono">{available.toLocaleString()}</span>
            {reserved > 0 && (
              <span className="text-warning/60 text-xs">(+{reserved.toLocaleString()})</span>
            )}
          </div>
        )}

        <ThemeToggle />

        <button
          className="btn-ghost"
          onClick={() => i18n.changeLanguage(i18n.language === 'ru' ? 'en' : i18n.language === 'en' ? 'ua' : 'ru')}
          title="Switch language"
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm uppercase">{i18n.language}</span>
        </button>

        <button className="btn-ghost relative">
          <Bell className="w-4 h-4" />
          {/* TODO Phase 2: badge с числом непрочитанных */}
        </button>

        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-accent-orange flex items-center justify-center text-white font-semibold text-sm overflow-hidden"
          >
            {account?.avatar_url ? (
              <img src={account.avatar_url} alt={account.full_name} className="w-full h-full object-cover" />
            ) : (
              initials || <User className="w-4 h-4" />
            )}
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 mt-2 w-56 rounded-xl bg-surface-900 border border-surface-800 shadow-xl z-50 overflow-hidden"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <div className="px-3 py-2 border-b border-surface-800">
                <div className="text-sm font-medium">{account?.full_name}</div>
                <div className="text-xs text-surface-500">{account?.email}</div>
                <div className="text-xs text-surface-500 mt-0.5">
                  {t('common.role')}: <span className="font-mono">{account?.role}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/settings/profile');
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-surface-800/60"
              >
                <User className="w-4 h-4 inline mr-2" /> {t('nav.profile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2 text-sm hover:bg-danger-tint/10 text-danger"
              >
                <LogOut className="w-4 h-4 inline mr-2" /> {t('common.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
